import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { MoreVertical, Plus, Pencil, Trash2, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@repo/ui/src/components/ui/badge';
import { Button } from '@repo/ui/src/components/ui/button';
import { Input } from '@repo/ui/src/components/ui/input';
import { Label } from '@repo/ui/src/components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@repo/ui/src/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@repo/ui/src/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@repo/ui/src/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@repo/ui/src/components/ui/alert-dialog';
import type { Subject } from '@repo/shared-types';
import {
  useSubjects, useCreateSubject, useUpdateSubject, useDeleteSubject,
} from '../../hooks/useSubjects';
import { useCan } from '../../hooks/useCan';
import { AdminOnlyTooltip } from '../../components/AdminOnlyTooltip';
import { PageHeader, pageHeaderActionClass } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';

export function SubjectsPage() {
  const { t } = useTranslation('students');
  const navigate = useNavigate();
  const { data: subjects, isLoading } = useSubjects();
  const [open, setOpen] = useState(false);
  const canManageSubjects = useCan('manage:subjects');
  // THI-129: MEMBER (assistant teacher) never sees tuition. The API already
  // nulls defaultRate/defaultMonthlyRate for them; drop the column entirely
  // rather than render a header over a column of "—".
  const canSeeMoney = useCan('manage:enrollments');

  return (
    // L container: this is a list screen, and every other list in the app sits
    // on max-w-6xl (01-shell §5). max-w-4xl was the last page off the scale.
    <div className="p-8 max-w-6xl mx-auto w-full">
      <PageHeader
        // Same scale as the container (L / max-w-6xl) — `md` pinned the header
        // to 896px while the table ran full width, so the title sat indented.
        size="lg"
        title={t('subjects.title')}
        subtitle={t('subjects.subtitle')}
        subtitleWrap
        extra={
          <AdminOnlyTooltip show={!canManageSubjects} className="flex-1 sm:flex-none">
            <Button
              size="sm"
              onClick={() => setOpen(true)}
              className={`${pageHeaderActionClass} gap-2`}
              disabled={!canManageSubjects}
            >
              <Plus className="h-4 w-4" /> {t('subjects.add')}
            </Button>
          </AdminOnlyTooltip>
        }
      />

      <div className="rounded-md border bg-card">
        {isLoading ? (
          <p className="flex h-24 items-center justify-center text-sm text-surface-500">...</p>
        ) : !subjects || subjects.length === 0 ? (
          <EmptyState
            kind="teach"
            icon={BookOpen}
            title={t('subjects.empty')}
            body={t('subjects.emptyBody')}
            action={canManageSubjects ? { label: t('subjects.create'), onClick: () => setOpen(true) } : undefined}
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                {/* Name takes the row; the rate is a fixed right-aligned number
                    column so "Tên môn" is not squeezed to a third of the table. */}
                <TableHead>{t('subjects.name')}</TableHead>
                {/* Both rates are fixed right-aligned number columns; the
                    monthly one and the template column fold under the name
                    on phones (a 390px row cannot hold five columns). */}
                {canSeeMoney && (
                  <TableHead className="hidden w-[160px] text-right sm:table-cell">{t('subjects.rateCol')}</TableHead>
                )}
                {canSeeMoney && (
                  <TableHead className="hidden w-[160px] text-right sm:table-cell">{t('subjects.monthlyRateCol')}</TableHead>
                )}
                <TableHead className="w-[120px] whitespace-nowrap sm:w-[150px]">{t('subjects.templateCol')}</TableHead>
                <TableHead className="w-[48px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {subjects.map((s) => (
                <SubjectRow key={s.id} subject={s} onNavigate={() => navigate(`/subjects/${s.id}`)} />
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <CreateSubjectDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}

/** "Âm nhạc" / "Tự tạo" for a subject with a template, muted "Chưa có"
 *  without one — the preset name comes from the progress namespace, the same
 *  key the subject page's meta line uses. */
function TemplateChip({ subject: s }: { subject: Subject }) {
  const { t } = useTranslation('students');
  const { t: tProgress } = useTranslation('progress');
  if (!s.noteTemplate) return <span className="text-[12.5px] text-surface-400">{t('subjects.templateNone')}</span>;
  const key = s.noteTemplate.presetKey;
  const label = key && key !== 'custom' ? tProgress(`template.preset.${key}`) : t('subjects.templateCustom');
  return <Badge variant="success">{label}</Badge>;
}

const money = (v: number | null) => (v != null ? v.toLocaleString('vi-VN') : '—');

function SubjectRow({ subject: s, onNavigate }: { subject: Subject; onNavigate: () => void }) {
  const { t } = useTranslation('students');
  const { mutate: del, isPending: isDeleting } = useDeleteSubject();
  const [editOpen, setEditOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const canManageSubjects = useCan('manage:subjects');
  const canSeeMoney = useCan('manage:enrollments');

  const handleDelete = () =>
    del(s.id, { onSuccess: () => setDeleteConfirmOpen(false) });

  return (
    <TableRow className="cursor-pointer" onClick={onNavigate}>
      <TableCell className="font-medium">
        {s.name}
        {/* Phone: both rates fold under the name; the sườn column stays. */}
        {canSeeMoney && (s.defaultRate != null || s.defaultMonthlyRate != null) && (
          <span className="mt-0.5 block text-[12px] font-normal tabular text-surface-500 sm:hidden">
            {s.defaultRate != null && <span className="block">{money(s.defaultRate)} {t('subjects.perSession')}</span>}
            {s.defaultMonthlyRate != null && <span className="block">{money(s.defaultMonthlyRate)} {t('subjects.perMonth')}</span>}
          </span>
        )}
      </TableCell>
      {canSeeMoney && (
        <TableCell className="hidden text-right tabular sm:table-cell">{money(s.defaultRate)}</TableCell>
      )}
      {canSeeMoney && (
        <TableCell className="hidden text-right tabular sm:table-cell">{money(s.defaultMonthlyRate)}</TableCell>
      )}
      <TableCell><TemplateChip subject={s} /></TableCell>
      <TableCell>
        <AdminOnlyTooltip show={!canManageSubjects}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('subjects.menu')}
              onClick={(e) => e.stopPropagation()}
              disabled={!canManageSubjects}
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            <DropdownMenuItem onClick={() => setEditOpen(true)}>
              <Pencil className="h-4 w-4" /> {t('subjects.edit')}
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-red-600 focus:text-red-600"
              onClick={() => setDeleteConfirmOpen(true)}
            >
              <Trash2 className="h-4 w-4" /> {t('subjects.delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        </AdminOnlyTooltip>

        <EditSubjectDialog subject={s} open={editOpen} onOpenChange={setEditOpen} />

        <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <AlertDialogContent onClick={(e) => e.stopPropagation()}>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('subjects.deleteConfirm.title')}</AlertDialogTitle>
              <AlertDialogDescription>{t('subjects.deleteConfirm.description')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('subjects.deleteConfirm.cancel')}</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={isDeleting}
                className="bg-red-600 hover:bg-red-700"
              >
                {t('subjects.deleteConfirm.confirm')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </TableCell>
    </TableRow>
  );
}

function CreateSubjectDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { t } = useTranslation('students');
  const { mutate: create, isPending } = useCreateSubject();
  const [name, setName] = useState('');
  const [rate, setRate] = useState('');
  const [monthlyRate, setMonthlyRate] = useState('');

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    create(
      {
        name,
        defaultRate: rate ? Number(rate) : null,
        defaultMonthlyRate: monthlyRate ? Number(monthlyRate) : null,
      },
      { onSuccess: () => { onOpenChange(false); setName(''); setRate(''); setMonthlyRate(''); } },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t('subjects.create')}</DialogTitle>
          <DialogDescription>{t('subjects.createDesc')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit}>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="sname">{t('subjects.name')}</Label>
              <Input id="sname" value={name} onChange={(e) => setName(e.target.value)} required />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="srate">{t('subjects.defaultRate')}</Label>
              <Input id="srate" type="number" min="0" value={rate} onChange={(e) => setRate(e.target.value)} />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="smonthlyrate">{t('subjects.defaultMonthlyRate')}</Label>
              <Input
                id="smonthlyrate"
                type="number"
                min="0"
                value={monthlyRate}
                onChange={(e) => setMonthlyRate(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t('subjects.cancel')}</Button>
            <Button type="submit" disabled={isPending || !name}>{t('subjects.submit')}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function EditSubjectDialog({
  subject, open, onOpenChange,
}: {
  subject: Subject;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const { t } = useTranslation('students');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>{t('subjects.editDialog.title')}</DialogTitle>
        </DialogHeader>
        {/* Mounted only while open so its fields always initialize fresh from the current subject
            (no effect-driven resync needed). */}
        {open && <EditSubjectForm subject={subject} onOpenChange={onOpenChange} />}
      </DialogContent>
    </Dialog>
  );
}

function EditSubjectForm({
  subject, onOpenChange,
}: {
  subject: Subject;
  onOpenChange: (o: boolean) => void;
}) {
  const { t } = useTranslation('students');
  const { mutate: update, isPending } = useUpdateSubject(subject.id);
  const [name, setName] = useState(subject.name);
  const [rate, setRate] = useState(subject.defaultRate != null ? String(subject.defaultRate) : '');
  const [monthlyRate, setMonthlyRate] = useState(
    subject.defaultMonthlyRate != null ? String(subject.defaultMonthlyRate) : '',
  );

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    update(
      {
        name,
        defaultRate: rate ? Number(rate) : null,
        defaultMonthlyRate: monthlyRate ? Number(monthlyRate) : null,
      },
      {
        onSuccess: () => {
          onOpenChange(false);
          toast.success(t('subjects.editDialog.success'));
        },
      },
    );
  };

  return (
    <form onSubmit={submit}>
      <div className="grid gap-4 py-4">
        <div className="grid gap-2">
          <Label htmlFor="ename">{t('subjects.editDialog.name')}</Label>
          <Input id="ename" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="erate">{t('subjects.editDialog.defaultRate')}</Label>
          <Input id="erate" type="number" min="0" value={rate} onChange={(e) => setRate(e.target.value)} />
          <p className="text-xs text-muted-foreground">{t('subjects.editDialog.rateHint')}</p>
        </div>
        <div className="grid gap-2">
          <Label htmlFor="emonthlyrate">{t('subjects.editDialog.defaultMonthlyRate')}</Label>
          <Input
            id="emonthlyrate"
            type="number"
            min="0"
            value={monthlyRate}
            onChange={(e) => setMonthlyRate(e.target.value)}
          />
        </div>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
          {t('subjects.editDialog.cancel')}
        </Button>
        <Button type="submit" disabled={isPending || !name}>
          {isPending ? t('subjects.editDialog.submitting') : t('subjects.editDialog.submit')}
        </Button>
      </DialogFooter>
    </form>
  );
}
